/**
 * `/api/crm` — every call the CRM module makes.
 *
 * In the module's folder rather than `src/api/`, so the module is one slice; it
 * still goes through `apiFetch`, so the 401 handling, the error shape and the
 * relative path are core's.
 */
import type {
  ActivityDue,
  ActivityListResponse,
  ActivityResponse,
  CreateActivityRequest,
  CreateDealRequest,
  CreateOrganizationRequest,
  CreatePersonRequest,
  CreateStageRequest,
  CrmBoardResponse,
  CrmHistoryResponse,
  CrmOwnerSearchResponse,
  DealListResponse,
  DealResponse,
  DealStatus,
  ListQueryParams,
  OkResponse,
  OrganizationListResponse,
  OrganizationResponse,
  PersonListResponse,
  PersonResponse,
  StageListResponse,
  StageResponse,
  UpdateActivityRequest,
  UpdateDealRequest,
  UpdateOrganizationRequest,
  UpdatePersonRequest,
  UpdateStageRequest,
} from '@forsys/shared';
import { apiFetch, query } from '../../api/client';

// --- board and stages --------------------------------------------------------

export const getBoard = () => apiFetch<CrmBoardResponse>('/crm/board');
export const listStages = () => apiFetch<StageListResponse>('/crm/stages');
export const createStage = (body: CreateStageRequest) =>
  apiFetch<StageResponse>('/crm/stages', { method: 'POST', json: body });
export const updateStage = (id: number, body: UpdateStageRequest) =>
  apiFetch<StageResponse>(`/crm/stages/${id}`, { method: 'PATCH', json: body });
export const reorderStages = (stageIds: number[]) =>
  apiFetch<StageListResponse>('/crm/stages/order', { method: 'PUT', json: { stageIds } });
export const deleteStage = (id: number) => apiFetch<OkResponse>(`/crm/stages/${id}`, { method: 'DELETE' });

// --- organizations -----------------------------------------------------------

export const listOrganizations = (params: ListQueryParams & { q?: string } = {}) =>
  apiFetch<OrganizationListResponse>(`/crm/organizations${query({ ...params })}`);
export const getOrganization = (id: number) => apiFetch<OrganizationResponse>(`/crm/organizations/${id}`);
export const createOrganization = (body: CreateOrganizationRequest) =>
  apiFetch<OrganizationResponse>('/crm/organizations', { method: 'POST', json: body });
export const updateOrganization = (id: number, body: UpdateOrganizationRequest) =>
  apiFetch<OrganizationResponse>(`/crm/organizations/${id}`, { method: 'PATCH', json: body });
export const deleteOrganization = (id: number) =>
  apiFetch<OkResponse>(`/crm/organizations/${id}`, { method: 'DELETE' });

// --- persons -----------------------------------------------------------------

export const listPersons = (params: ListQueryParams & { q?: string; organizationId?: number } = {}) =>
  apiFetch<PersonListResponse>(`/crm/persons${query({ ...params })}`);
export const getPerson = (id: number) => apiFetch<PersonResponse>(`/crm/persons/${id}`);
export const createPerson = (body: CreatePersonRequest) =>
  apiFetch<PersonResponse>('/crm/persons', { method: 'POST', json: body });
export const updatePerson = (id: number, body: UpdatePersonRequest) =>
  apiFetch<PersonResponse>(`/crm/persons/${id}`, { method: 'PATCH', json: body });
export const deletePerson = (id: number) => apiFetch<OkResponse>(`/crm/persons/${id}`, { method: 'DELETE' });

// --- deals -------------------------------------------------------------------

export interface DealListParams extends ListQueryParams {
  status?: DealStatus | 'all';
  q?: string;
  stageId?: number;
  ownerId?: number;
  personId?: number;
  organizationId?: number;
}

export const listDeals = (params: DealListParams = {}) =>
  apiFetch<DealListResponse>(`/crm/deals${query({ ...params })}`);
export const getDeal = (id: number) => apiFetch<DealResponse>(`/crm/deals/${id}`);
export const createDeal = (body: CreateDealRequest) =>
  apiFetch<DealResponse>('/crm/deals', { method: 'POST', json: body });
export const updateDeal = (id: number, body: UpdateDealRequest) =>
  apiFetch<DealResponse>(`/crm/deals/${id}`, { method: 'PATCH', json: body });
export const moveDeal = (id: number, stageId: number) =>
  apiFetch<DealResponse>(`/crm/deals/${id}/move`, { method: 'POST', json: { stageId } });
export const winDeal = (id: number) => apiFetch<DealResponse>(`/crm/deals/${id}/win`, { method: 'POST' });
export const loseDeal = (id: number, reason: string | null) =>
  apiFetch<DealResponse>(`/crm/deals/${id}/lose`, { method: 'POST', json: { reason } });
export const reopenDeal = (id: number) => apiFetch<DealResponse>(`/crm/deals/${id}/reopen`, { method: 'POST' });
export const deleteDeal = (id: number) => apiFetch<OkResponse>(`/crm/deals/${id}`, { method: 'DELETE' });
export const getDealHistory = (id: number, beforeId?: string) =>
  apiFetch<CrmHistoryResponse>(`/crm/deals/${id}/history${query({ beforeId })}`);

// --- activities --------------------------------------------------------------

export interface ActivityListParams extends ListQueryParams {
  due?: ActivityDue;
  ownerId?: number;
  dealId?: number;
  personId?: number;
  organizationId?: number;
}

/** The caller's today, `YYYY-MM-DD` — the server does not know the time zone. */
export function today(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export const listActivities = (params: ActivityListParams = {}) =>
  apiFetch<ActivityListResponse>(`/crm/activities${query({ ...params, today: today() })}`);
export const createActivity = (body: CreateActivityRequest) =>
  apiFetch<ActivityResponse>('/crm/activities', { method: 'POST', json: body });
export const updateActivity = (id: number, body: UpdateActivityRequest) =>
  apiFetch<ActivityResponse>(`/crm/activities/${id}`, { method: 'PATCH', json: body });
export const setActivityDone = (id: number, done: boolean) =>
  apiFetch<ActivityResponse>(`/crm/activities/${id}/${done ? 'done' : 'undone'}`, { method: 'POST' });
export const deleteActivity = (id: number) => apiFetch<OkResponse>(`/crm/activities/${id}`, { method: 'DELETE' });

// --- owners ------------------------------------------------------------------

export const searchOwners = (q: string) => apiFetch<CrmOwnerSearchResponse>(`/crm/owners${query({ q })}`);
